import { useCallback, useEffect, useState } from "react";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { paths, type UiPrefs } from "@clevis/shared-types";
import { db } from "../lib/firebase";
import { useAuth } from "./useAuth";

// A person's own layout: menu order, favorites, dashboard widgets. Stored on
// their account so it follows them from the office PC to their phone.
export function useUiPrefs(): { prefs: UiPrefs; loaded: boolean; save: (patch: Partial<UiPrefs>) => void } {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<UiPrefs>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user) return;
    return onSnapshot(
      doc(db, paths.userUiPrefs(user.uid)),
      (snap) => {
        setPrefs((snap.data() as UiPrefs | undefined) ?? {});
        setLoaded(true);
      },
      () => setLoaded(true),
    );
  }, [user]);

  const save = useCallback(
    (patch: Partial<UiPrefs>) => {
      if (!user) return;
      setPrefs((p) => ({ ...p, ...patch })); // instant; the snapshot confirms
      void setDoc(doc(db, paths.userUiPrefs(user.uid)), { ...patch, updatedAt: Date.now() }, { merge: true }).catch(() => undefined);
    },
    [user],
  );

  return { prefs, loaded, save };
}
